/**
 * LazyChart - Centralized Recharts Wrapper
 * Sprint C.14 Priority 2 - Phase 2 - Frontend Optimization C1
 * 
 * Benefits:
 * - Single lazy loading point for recharts (~250KB)
 * - Unified API for all chart types
 * - Centralized Loading/Error states
 * - Reduced bundle size on initial load
 * - Easy maintenance and evolution
 */

import React, { lazy, Suspense } from 'react';
import { Loader2 } from 'lucide-react';

// Lazy load recharts library once
const RechartsComponents = lazy(() => 
  import('recharts').then(module => ({
    default: {
      LineChart: module.LineChart,
      Line: module.Line,
      BarChart: module.BarChart,
      Bar: module.Bar,
      PieChart: module.PieChart,
      Pie: module.Pie,
      Cell: module.Cell,
      XAxis: module.XAxis,
      YAxis: module.YAxis,
      CartesianGrid: module.CartesianGrid,
      Tooltip: module.Tooltip,
      Legend: module.Legend,
      ResponsiveContainer: module.ResponsiveContainer,
      AreaChart: module.AreaChart,
      Area: module.Area,
    }
  }))
);

/**
 * Chart Loading Skeleton
 */
const ChartSkeleton = ({ height = 300 }) => (
  <div 
    className="flex items-center justify-center bg-zinc-900/50 rounded-lg animate-pulse"
    style={{ height: `${height}px` }}
  >
    <div className="text-center">
      <Loader2 className="h-8 w-8 animate-spin text-zinc-600 mx-auto mb-2" />
      <p className="text-sm text-zinc-500">Chargement du graphique...</p>
    </div>
  </div>
);

/**
 * LazyChart Component
 * 
 * @param {string} type - Chart type: 'line', 'bar', 'pie', 'area'
 * @param {Array} data - Chart data
 * @param {Object} config - Chart configuration
 * @param {number} height - Chart height (default: 300)
 * @param {Object} style - Additional styles
 */
const LazyChart = ({ type = 'line', data = [], config = {}, height = 300, style = {} }) => {
  return (
    <Suspense fallback={<ChartSkeleton height={height} />}>
      <RechartsComponents>
        {(Components) => {
          const { ResponsiveContainer } = Components;

          return (
            <ResponsiveContainer width="100%" height={height} style={style}>
              {type === 'line' && <LineChartComponent data={data} config={config} Components={Components} />}
              {type === 'bar' && <BarChartComponent data={data} config={config} Components={Components} />}
              {type === 'pie' && <PieChartComponent data={data} config={config} Components={Components} />}
              {type === 'area' && <AreaChartComponent data={data} config={config} Components={Components} />}
            </ResponsiveContainer>
          );
        }}
      </RechartsComponents>
    </Suspense>
  );
};

/**
 * Line Chart Component
 */
const LineChartComponent = ({ data, config, Components }) => {
  const { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } = Components;
  const { lines = [], xKey = 'date', showGrid = true, showLegend = true } = config;

  return (
    <LineChart data={data}>
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />}
      <XAxis dataKey={xKey} stroke="#71717A" />
      <YAxis stroke="#71717A" />
      <Tooltip 
        contentStyle={{ 
          backgroundColor: '#18181B', 
          border: '1px solid #27272A',
          borderRadius: '8px',
          color: '#F4F4F5'
        }} 
      />
      {showLegend && <Legend />}
      {lines.map((line, idx) => (
        <Line 
          key={idx}
          type="monotone"
          dataKey={line.dataKey}
          stroke={line.stroke || '#6366f1'}
          strokeWidth={line.strokeWidth || 2}
          name={line.name || line.dataKey}
          dot={line.dot !== undefined ? line.dot : false}
        />
      ))}
    </LineChart>
  );
};

/**
 * Bar Chart Component
 */
const BarChartComponent = ({ data, config, Components }) => {
  const { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } = Components;
  const { bars = [], xKey = 'name', showGrid = true, showLegend = true } = config;

  return (
    <BarChart data={data}>
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />}
      <XAxis dataKey={xKey} stroke="#71717A" />
      <YAxis stroke="#71717A" />
      <Tooltip 
        contentStyle={{ 
          backgroundColor: '#18181B', 
          border: '1px solid #27272A',
          borderRadius: '8px',
          color: '#F4F4F5'
        }} 
      />
      {showLegend && <Legend />}
      {bars.map((bar, idx) => (
        <Bar 
          key={idx}
          dataKey={bar.dataKey}
          fill={bar.fill || '#6366f1'}
          name={bar.name || bar.dataKey}
        />
      ))}
    </BarChart>
  );
};

/**
 * Pie Chart Component
 */
const PieChartComponent = ({ data, config, Components }) => {
  const { PieChart, Pie, Cell, Tooltip } = Components;
  const { dataKey = 'value', nameKey = 'name', colors = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981'] } = config;

  return (
    <PieChart>
      <Pie
        data={data}
        cx="50%"
        cy="50%"
        labelLine={false}
        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        outerRadius={80}
        fill="#8884d8"
        dataKey={dataKey}
        nameKey={nameKey}
      >
        {data.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
        ))}
      </Pie>
      <Tooltip 
        contentStyle={{ 
          backgroundColor: '#18181B', 
          border: '1px solid #27272A',
          borderRadius: '8px',
          color: '#F4F4F5'
        }} 
      />
    </PieChart>
  );
};

/**
 * Area Chart Component
 */
const AreaChartComponent = ({ data, config, Components }) => {
  const { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend } = Components;
  const { areas = [], xKey = 'date', showGrid = true, showLegend = true } = config;

  return (
    <AreaChart data={data}>
      {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />}
      <XAxis dataKey={xKey} stroke="#71717A" />
      <YAxis stroke="#71717A" />
      <Tooltip 
        contentStyle={{ 
          backgroundColor: '#18181B', 
          border: '1px solid #27272A',
          borderRadius: '8px',
          color: '#F4F4F5'
        }} 
      />
      {showLegend && <Legend />}
      {areas.map((area, idx) => (
        <Area 
          key={idx}
          type="monotone"
          dataKey={area.dataKey}
          stroke={area.stroke || '#6366f1'}
          fill={area.fill || '#6366f1'}
          fillOpacity={area.fillOpacity || 0.3}
          name={area.name || area.dataKey}
        />
      ))}
    </AreaChart>
  );
};

export default LazyChart;

/**
 * Usage Examples:
 * 
 * // Line Chart
 * <LazyChart 
 *   type="line"
 *   data={timelineData}
 *   config={{
 *     xKey: 'date',
 *     lines: [
 *       { dataKey: 'likes', stroke: '#6366f1', name: 'Likes' },
 *       { dataKey: 'shares', stroke: '#8b5cf6', name: 'Partages' }
 *     ]
 *   }}
 *   height={300}
 * />
 * 
 * // Bar Chart
 * <LazyChart 
 *   type="bar"
 *   data={platformData}
 *   config={{
 *     xKey: 'name',
 *     bars: [
 *       { dataKey: 'posts', fill: '#6366f1', name: 'Posts' },
 *       { dataKey: 'engagement', fill: '#8b5cf6', name: 'Engagement' }
 *     ]
 *   }}
 *   height={300}
 * />
 * 
 * // Pie Chart
 * <LazyChart 
 *   type="pie"
 *   data={distributionData}
 *   config={{
 *     dataKey: 'posts',
 *     nameKey: 'name',
 *     colors: ['#6366f1', '#8b5cf6', '#ec4899']
 *   }}
 *   height={300}
 * />
 */
